<template>
  <v-row class="match-height mt-4">
    <!-- Date Range Filter -->
    <v-col cols="12">
      <v-card>
        <v-card-text>
          <v-row align="center">
            <v-col
              cols="12"
              sm="4"
            >
              <v-menu
                v-model="fromDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="fromDate"
                    label="From Date"
                    :prepend-icon="icons.mdiCalendar"
                    readonly
                    outlined
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="fromDate"
                  @input="fromDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <v-menu
                v-model="toDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template #activator="{ on, attrs }">
                  <v-text-field
                    v-model="toDate"
                    label="To Date"
                    :prepend-icon="icons.mdiCalendar"
                    readonly
                    outlined
                    dense
                    hide-details
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="toDate"
                  @input="toDateMenu = false"
                ></v-date-picker>
              </v-menu>
            </v-col>
            <v-col
              cols="12"
              sm="4"
            >
              <v-btn
                color="primary"
                :loading="isLoading"
                @click="fetchData"
              >
                Apply Filter
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Summary KPI Cards -->
    <v-col
      cols="12"
      sm="6"
      md="3"
    >
      <v-card>
        <v-card-text>
          <div class="d-flex align-center">
            <v-avatar
              color="primary"
              size="44"
              class="me-3"
            >
              <v-icon dark>
                {{ icons.mdiWater }}
              </v-icon>
            </v-avatar>
            <div>
              <p class="text-xs mb-0 text--secondary">
                Total Inflow
              </p>
              <span v-if="isLoading"><v-progress-circular
                indeterminate
                size="20"
                width="2"
              ></v-progress-circular></span>
              <span
                v-else
                class="text-xl font-weight-semibold"
              >{{ formatReading(summary.total_inflow) }} m³</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col
      cols="12"
      sm="6"
      md="3"
    >
      <v-card>
        <v-card-text>
          <div class="d-flex align-center">
            <v-avatar
              color="success"
              size="44"
              class="me-3"
            >
              <v-icon dark>
                {{ icons.mdiWaterPump }}
              </v-icon>
            </v-avatar>
            <div>
              <p class="text-xs mb-0 text--secondary">
                Total Distribution
              </p>
              <span v-if="isLoading"><v-progress-circular
                indeterminate
                size="20"
                width="2"
              ></v-progress-circular></span>
              <span
                v-else
                class="text-xl font-weight-semibold"
              >{{ formatReading(summary.total_outflow) }} m³</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col
      cols="12"
      sm="6"
      md="3"
    >
      <v-card>
        <v-card-text>
          <div class="d-flex align-center">
            <v-avatar
              color="warning"
              size="44"
              class="me-3"
            >
              <v-icon dark>
                {{ icons.mdiWaterAlert }}
              </v-icon>
            </v-avatar>
            <div>
              <p class="text-xs mb-0 text--secondary">
                Water Loss (NRW)
              </p>
              <span v-if="isLoading"><v-progress-circular
                indeterminate
                size="20"
                width="2"
              ></v-progress-circular></span>
              <span
                v-else
                class="text-xl font-weight-semibold"
              >{{ formatReading(summary.loss) }} m³</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-col>

    <v-col
      cols="12"
      sm="6"
      md="3"
    >
      <v-card>
        <v-card-text>
          <div class="d-flex align-center">
            <v-avatar
              :color="lossPercentageColor"
              size="44"
              class="me-3"
            >
              <v-icon dark>
                {{ icons.mdiPercent }}
              </v-icon>
            </v-avatar>
            <div>
              <p class="text-xs mb-0 text--secondary">
                Loss Percentage
              </p>
              <span v-if="isLoading"><v-progress-circular
                indeterminate
                size="20"
                width="2"
              ></v-progress-circular></span>
              <span
                v-else
                class="text-xl font-weight-semibold"
                :class="lossPercentageColor + '--text'"
              >
                {{ summary.loss_percentage }}%
              </span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-col>

    <!-- Inflow vs Distribution Comparison Chart -->
    <v-col cols="12">
      <v-card min-height="430">
        <v-card-title>Inflow vs Distribution Comparison</v-card-title>
        <v-card-text
          v-if="isLoading"
          class="d-flex justify-center align-center"
          style="min-height: 350px;"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-card-text>
        <v-card-text v-else-if="chartSeries[0].data.length === 0 && chartSeries[1].data.length === 0">
          <p class="font-weight-light">
            No data available for the selected period. Ensure main meters are tagged with a role (source/distribution) and have readings in this date range.
          </p>
        </v-card-text>
        <div v-else>
          <apexchart
            type="area"
            height="380"
            :options="chartOptions"
            :series="chartSeries"
          ></apexchart>
        </div>
      </v-card>
    </v-col>

    <!-- Individual Source Meters -->
    <v-col
      cols="12"
      md="6"
    >
      <v-card min-height="380">
        <v-card-title>
          <v-icon
            class="me-2"
            color="primary"
          >
            {{ icons.mdiWater }}
          </v-icon>
          Source Meters (Inflow)
        </v-card-title>
        <v-card-text
          v-if="isLoading"
          class="d-flex justify-center align-center"
          style="min-height: 300px;"
        >
          <v-progress-circular
            indeterminate
            color="primary"
          ></v-progress-circular>
        </v-card-text>
        <v-card-text v-else-if="sourceMeters.length === 0">
          <p class="font-weight-light">
            No source meters configured. Edit a main meter and set its role to "Source".
          </p>
        </v-card-text>
        <div v-else>
          <apexchart
            type="line"
            height="330"
            :options="sourceMeterChartOptions"
            :series="sourceMeterSeries"
          ></apexchart>
        </div>
      </v-card>
    </v-col>

    <!-- Individual Distribution Meters -->
    <v-col
      cols="12"
      md="6"
    >
      <v-card min-height="380">
        <v-card-title>
          <v-icon
            class="me-2"
            color="success"
          >
            {{ icons.mdiWaterPump }}
          </v-icon>
          Distribution Meters (Outflow)
        </v-card-title>
        <v-card-text
          v-if="isLoading"
          class="d-flex justify-center align-center"
          style="min-height: 300px;"
        >
          <v-progress-circular
            indeterminate
            color="success"
          ></v-progress-circular>
        </v-card-text>
        <v-card-text v-else-if="distributionMeters.length === 0">
          <p class="font-weight-light">
            No distribution meters configured. Edit a main meter and set its role to "Distribution".
          </p>
        </v-card-text>
        <div v-else>
          <apexchart
            type="line"
            height="330"
            :options="distributionMeterChartOptions"
            :series="distributionMeterSeries"
          ></apexchart>
        </div>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import axios from 'axios'
import VueApexCharts from 'vue-apexcharts'
import {
  mdiCalendar,
  mdiWater,
  mdiWaterPump,
  mdiWaterAlert,
  mdiPercent,
} from '@mdi/js'

export default {
  name: 'MainMeterStatistics',
  components: {
    apexchart: VueApexCharts,
  },
  setup() {
    return {
      icons: {
        mdiCalendar,
        mdiWater,
        mdiWaterPump,
        mdiWaterAlert,
        mdiPercent,
      },
    }
  },
  data() {
    const today = new Date()
    const thirtyDaysAgo = new Date()
    thirtyDaysAgo.setDate(today.getDate() - 30)

    return {
      fromDate: thirtyDaysAgo.toISOString().substring(0, 10),
      toDate: today.toISOString().substring(0, 10),
      fromDateMenu: false,
      toDateMenu: false,
      isLoading: false,
      summary: {
        total_inflow: 0,
        total_outflow: 0,
        loss: 0,
        loss_percentage: 0,
      },
      chartSeries: [
        { name: 'Total Inflow', data: [] },
        { name: 'Total Distribution', data: [] },
      ],
      sourceMeters: [],
      distributionMeters: [],
    }
  },
  computed: {
    lossPercentageColor() {
      const pct = this.summary.loss_percentage
      if (pct < 10) return 'success'
      if (pct < 25) return 'warning'

      return 'error'
    },
    chartOptions() {
      return {
        chart: {
          type: 'area',
          toolbar: { show: true },
          zoom: { enabled: true },
        },
        colors: ['#1976D2', '#388E3C'],
        stroke: { curve: 'smooth', width: 2 },
        fill: {
          type: 'gradient',
          gradient: { opacityFrom: 0.3, opacityTo: 0.05 },
        },
        xaxis: {
          type: 'datetime',
          labels: { datetimeUTC: false },
        },
        yaxis: {
          title: { text: 'Consumption (m³)' },
          labels: {
            formatter: val => `${Number(val).toFixed(1)} m³`,
          },
        },
        tooltip: {
          x: { format: 'dd MMM yyyy' },
          y: { formatter: val => `${Number(val).toFixed(3)} m³` },
        },
        legend: { position: 'top' },
        dataLabels: { enabled: false },
      }
    },
    sourceMeterSeries() {
      return this.sourceMeters.map(m => ({
        name: m.description || m.meter_number,
        data: m.data,
      }))
    },
    distributionMeterSeries() {
      return this.distributionMeters.map(m => ({
        name: m.description || m.meter_number,
        data: m.data,
      }))
    },
    sourceMeterChartOptions() {
      return this.buildMeterChartOptions()
    },
    distributionMeterChartOptions() {
      return this.buildMeterChartOptions()
    },
  },
  mounted() {
    this.fetchData()
  },
  methods: {
    fetchData() {
      this.isLoading = true
      axios
        .get('statistics/main-meters/comparison', {
          params: {
            fromDate: `${this.fromDate} 00:00:00`,
            toDate: `${this.toDate} 23:59:59`,
          },
        })
        .then(response => {
          const { data } = response
          this.summary = data.summary
          this.chartSeries = data.chart_series
          this.sourceMeters = data.source_meters
          this.distributionMeters = data.distribution_meters
        })
        .catch(error => {
          console.error(error)
          this.$notification.error(error.response?.data?.message || 'Failed to load main meter data')
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    formatReading(value) {
      if (!value && value !== 0) return '—'

      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 3 })
    },
    buildMeterChartOptions() {
      return {
        chart: {
          type: 'line',
          toolbar: { show: true },
          zoom: { enabled: true },
        },
        stroke: { curve: 'smooth', width: 2 },
        xaxis: {
          type: 'datetime',
          labels: { datetimeUTC: false },
        },
        yaxis: {
          title: { text: 'Consumption (m³)' },
          labels: {
            formatter: val => `${Number(val).toFixed(1)} m³`,
          },
        },
        tooltip: {
          x: { format: 'dd MMM yyyy' },
          y: { formatter: val => `${Number(val).toFixed(3)} m³` },
        },
        legend: { position: 'top' },
        dataLabels: { enabled: false },
      }
    },
  },
}
</script>
